import { Trans, useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { TeamMember, TeamMemberListItem } from '@/modules/team/types/team-member';

import { useDeleteTeamMembersBulk } from '../../hooks/use-delete-team-members-bulk';

const MAX_VISIBLE_MEMBERS = 5;

interface DeleteTeamMembersModalProps {
  teamMembersForDelete: (TeamMember | TeamMemberListItem)[];
  onSuccess: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * Modal for permanently deleting team members with confirmation workflow.
 * Supports both single and bulk deletion operations.
 *
 * Mirrors the project deletion modal functionality but adapted for team members.
 */
export function DeleteTeamMembersModal({
  teamMembersForDelete,
  onSuccess,
  trigger,
  open,
  onOpen,
  onClose,
}: DeleteTeamMembersModalProps) {
  const { t } = useTranslation(['authenticated.team', 'common']);

  const memberNames = teamMembersForDelete.map(m => m.name);
  const visibleMemberNames = memberNames.slice(0, MAX_VISIBLE_MEMBERS);
  const remainingMemberNames = memberNames.slice(MAX_VISIBLE_MEMBERS);
  const remainingCount = remainingMemberNames.length;

  const memberIds = teamMembersForDelete.map(m => Number(m.id)).filter(Boolean);

  const deleteTeamMembersMutation = useDeleteTeamMembersBulk({ onSuccess });

  const handleDelete = async (closeModal: () => void) => {
    try {
      await deleteTeamMembersMutation.mutateAsync(memberIds);
      closeModal();
    } catch (error) {
      // Error handled by hook, keep modal open for retry
    }
  };

  const description = (
    <div>
      <Trans
        i18nKey="authenticated.team:delete.deleteTeamMembersMessage"
        count={teamMembersForDelete.length}
        values={{
          members: visibleMemberNames.join(', '),
          member: visibleMemberNames.join(', '),
        }}
        components={{
          bold: <strong />,
        }}
      />
      {remainingCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help font-bold">
              {t('authenticated.team:delete.andMoreMembers', { count: remainingCount })}
            </span>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent className="max-w-md">
              <p>{remainingMemberNames.join(', ')}</p>
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      )}
    </div>
  );

  const additionalContent = (
    <div className="space-y-4 text-sm text-muted-foreground">
      <div>
        <p className="font-bold">{t('authenticated.team:delete.attention')}</p>
        <ul className="ml-4 list-inside list-disc">
          <li>{t('authenticated.team:delete.memberInformation')}</li>
          <li>{t('authenticated.team:delete.assignments')}</li>
          <li>{t('authenticated.team:delete.timeTracks')}</li>
        </ul>
      </div>
      <p className="font-medium">{t('authenticated.team:delete.warningFooter')}</p>
    </div>
  );

  return (
    <ConfirmationModal
      trigger={trigger}
      title={t('authenticated.team:delete.deleteTeamMembers', {
        count: teamMembersForDelete.length,
      })}
      message={description}
      confirmText={
        deleteTeamMembersMutation.isPending
          ? t('authenticated.team:delete.deleting')
          : t('authenticated.team:delete.deleteTeamMembersButton', {
              count: teamMembersForDelete.length,
            })
      }
      confirmVariant="destructive"
      onConfirm={handleDelete}
      isLoading={deleteTeamMembersMutation.isPending}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
    >
      {additionalContent}
    </ConfirmationModal>
  );
}
